import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './ui/input';

/**
 * Password input with a show/hide toggle eye icon.
 *
 * Drop-in replacement for `<Input type="password" />` — accepts all the same
 * props (id, value, onChange, placeholder, required, minLength, data-testid…)
 * and adds an Eye / EyeOff button on the right side of the input that toggles
 * between `type="password"` and `type="text"` so end users can verify what
 * they typed.
 *
 * Test IDs: the toggle button gets `<base-testid>-toggle` so frontend tests
 * can drive it. e.g. data-testid="login-password-input" →
 * "login-password-input-toggle" on the toggle.
 */
export const PasswordInput = React.forwardRef(function PasswordInput(
  { className = '', 'data-testid': testId, ...props },
  ref
) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        // Reserve room on the right so long passwords don't slide under the icon.
        className={`pr-10 ${className}`}
        data-testid={testId}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
        data-testid={testId ? `${testId}-toggle` : 'password-toggle'}
        tabIndex={-1}
      >
        <Icon className="w-4 h-4" />
      </button>
    </div>
  );
});
